<template>
  <div class="memorial-form__form-container contact-form" v-if="showForm">
    <Loader :loading="isLoading" />

    <transition name="contact-form__reveal-error">
      <p
        class="memorial-form__error"
        v-if="formError && order"
        v-html="formError"
      />
      <p v-else-if="formError && !order">
        {{ texts.formLoadError }}
      </p>
    </transition>

    <transition mode="out-in" name="contact-form__fade">
      <form v-if="order && isOpened && !submitted" @submit.prevent="rsvp">
        <form-input
          ref="firstName"
          v-model="firstName"
          :label="texts.formPlaceholderFirstName"
          :name="'firstName'"
          :required="true"
          :type="'text'"
          @trigger-field-error="triggerFieldError"
        />

        <form-input
          ref="lastName"
          v-model="lastName"
          :label="texts.formPlaceholderLastName"
          :name="'lastName'"
          :required="true"
          :type="'text'"
          @trigger-field-error="triggerFieldError"
        />

        <form-input
          ref="email"
          v-model="email"
          :label="texts.formPlaceholderEmail"
          :name="'email'"
          :type="'email'"
          @trigger-field-error="triggerFieldError"
        />

        <form-input
          ref="tel"
          v-model="tel"
          :label="texts.formPlaceholderMobilePhone"
          :name="'tel'"
          :required="true"
          :type="'tel'"
          @trigger-field-error="triggerFieldError"
        />

        <form-input
          ref="numAttendees"
          v-model="numAttendees"
          :label="texts.formPlaceholderNumAttendees"
          :name="'numAttendees'"
          :required="true"
          :type="'number'"
          @trigger-field-error="triggerFieldError"
        />

        <form-textarea
          v-model="text"
          :label="texts.formPlaceholderMessage"
          :name="'text'"
          :rows="'1'"
        />

        <div class="checkbox contact-form__accept" data-cy="cf-accept-checkbox">
          <input
            id="contact-form__accept-rsvp"
            required
            type="checkbox"
            v-model="accept"
          >
          <!-- biome-ignore lint/a11y/noLabelWithoutControl: False positive -->
          <label
            class="marker"
            for="contact-form__accept-rsvp"
            v-html="texts.acceptText"
          />
        </div>
        <div class="contact-form__submit">
          <button
            class="btn obituary cta"
            type="submit"
            :disabled="disabled"
            v-text="texts.formSubmit"
          />

          <button
            class="btn obituary icon-right"
            type="button"
            @click.prevent="toggleIsOpened($event, false)"
          >
            {{ texts.formHide }}
            <svg class="icon">
              <title>Close</title>
              <use href="#chevron-up" />
            </svg>
          </button>
        </div>
      </form>

      <div v-else-if="!((isOpened || submitted) || formError)">
        <button
          class="btn obituary icon-right"
          type="button"
          @click.prevent="toggleIsOpened($event, true)"
        >
          {{ texts.formShowRSVP }}
          <svg class="icon">
            <title>Open</title>
            <use href="#chevron-down" />
          </svg>
        </button>
      </div>

      <div v-else-if="submitted">
        <p class="memorial-form__response" v-html="responseMessage" />
        <a class="btn obituary" v-if="guestLink" :href="guestLink">
          {{ texts.formGuestLink }}
        </a>
      </div>
    </transition>
  </div>
</template>

<script>
  import FormInput from "Components/FormInput.vue";
  import FormTextarea from "Components/FormTextarea.vue";
  import Loader from "Components/loader/Loader.vue";
  import { parse } from "date-fns";
  import { sprintf } from "sprintf-js";
  import { mapState } from "vuex";

  export default {
    components: {
      FormInput,
      FormTextarea,
      Loader,
    },

    computed: {
      ...mapState("orders", [
        "order",
        "isSubmitting",
        "error",
        "submitted",
        "guestLink",
      ]),

      canRSVP() {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // make sure we dont compare time.

        return this.rsvpDate >= today;
      },

      disabled() {
        for (const field of this.requiredFields) {
          if (this[field] === "") {
            return true;
          }
        }

        return !this.accept;
      },

      formError() {
        return this.error;
      },

      // The order comes from /external-orders, whose memorial block is camelCase.
      hasMaxAttendees() {
        return this.order.memorial.maxAttendees > 0;
      },

      isLoading() {
        return this.componentIsLoading || this.isSubmitting;
      },

      responseMessage() {
        if (!(this.submitted && this.order?.memorial)) {
          return;
        }

        if (
          !this.hasMaxAttendees ||
          this.order.memorial.maxAttendees >=
            this.order.memorial.numAttendees +
              Number.parseInt(this.numAttendees, 10)
        ) {
          return sprintf(this.texts.formSuccessMessage, this.numAttendees);
        }
        return this.texts.formSuccessMessageQueue;
      },

      rsvpDate() {
        if (this.dataRSVP) {
          return this.parse(this.dataRSVP, "yyyy-MM-dd", new Date());
        }

        const date = this.parse(this.startDate, "yyyy-MM-dd H:m:s", new Date());
        date.setDate(date.getDate() - 1);

        return date;
      },

      showForm() {
        return this.canRSVP;
      },
    },

    data() {
      const { dataset } = this.$mountEl;

      return {
        accept: false,
        componentIsLoading: false,
        dataRSVP: dataset.rsvp,
        email: "",
        firstName: "",
        hasFieldError: false,
        isOpened: false,
        lastName: "",
        numAttendees: "",
        obituaryId: dataset.obituaryId,
        requiredFields: ["firstName", "lastName", "tel", "numAttendees"],
        startDate: dataset.startDate,
        tel: "",
        termsUrl: dataset.termsUrl,
        text: "",
        texts: lavendlaObituary.rsvp,
        validate: ["firstName", "lastName", "email", "tel", "numAttendees"],
      };
    },

    methods: {
      parse,

      rsvp() {
        if (this.disabled) {
          return;
        }

        this.validateFields();

        if (this.hasFieldError) {
          this.componentIsLoading = false;
          return;
        }

        const data = {
          email: this.email,
          firstName: this.firstName,
          lastName: this.lastName,
          numAttendees: this.numAttendees,
          obituaryId: this.obituaryId,
          tel: this.tel,
          text: this.text,
        };

        this.$store
          .dispatch("orders/rsvp", data)
          .then(() => {
            this.componentIsLoading = false;
          })
          .catch(() => {
            this.componentIsLoading = false;
          });
      },

      toggleIsOpened(_e, bool) {
        this.isOpened = bool;
      },

      triggerFieldError(bool = true) {
        this.hasFieldError = bool;
      },

      validateFields() {
        this.hasFieldError = false;
        this.componentIsLoading = true;

        for (const requiredField of this.requiredFields) {
          if (this.$refs[requiredField]) {
            this.$refs[requiredField].validate();
          }
        }
      },
    },
    name: "RsvpForm",
  };
</script>
